/**
 * crossDomainRedirect
 * 
 * Shared utility for cross-domain session transfer.
 * Used by DomainEnforcer and DeterministicRouter.
 * 
 * Creates a short-lived session token via edge function,
 * appends it to the target URL, then redirects.
 * Falls through to plain redirect if unauthenticated or on error.
 */

import { supabase } from '@/integrations/supabase/client';
import { isDevDomain } from '@/constants/branding';
import { CROSS_DOMAIN_LOCALE_PARAM, CROSS_DOMAIN_LOCATION_PARAM } from '@/lib/crossDomainState';
import { logger } from '@/utils/logger';

export interface CrossDomainRedirectState {
  locale?: string;
  locationId?: string;
}

function buildTargetUrl(targetUrl: string, state?: CrossDomainRedirectState): URL {
  const url = new URL(targetUrl);

  if (state?.locale) {
    url.searchParams.set(CROSS_DOMAIN_LOCALE_PARAM, state.locale);
  }

  if (state?.locationId) {
    url.searchParams.set(CROSS_DOMAIN_LOCATION_PARAM, state.locationId);
  }

  return url;
}

export async function redirectWithSession(targetUrl: string, state?: CrossDomainRedirectState): Promise<void> {
  const redirectUrl = buildTargetUrl(targetUrl, state);

  // On dev/Lovable preview domains, skip token creation — the fetch proxy
  // intercepts Supabase auth requests, so the edge function call will fail.
  if (isDevDomain()) {
    logger.info('[CrossDomain] Dev domain detected, skipping session token creation');
    window.location.replace(redirectUrl.toString());
    return;
  }

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session) {
      const { data, error } = await supabase.functions.invoke('create-session-token');
      if (!error && data?.success && data?.data?.token) {
        redirectUrl.searchParams.set('session_token', data.data.token);
        window.location.replace(redirectUrl.toString());
        return;
      }
    }
  } catch {
    // Fall through to redirect without token
  }
  window.location.replace(redirectUrl.toString());
}
